import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

/** Reconhece caixas de busca pelo tipo ou pelo texto de exemplo. */
const ehBusca = (type?: string, placeholder?: string) =>
  type === "search" || /^(buscar|pesquisar|filtrar|digite|ex\.)/i.test(placeholder ?? "");

const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, type, ...props }, ref) => {
    const interno = React.useRef<HTMLInputElement | null>(null);
    const setRef = (el: HTMLInputElement | null) => {
      interno.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
    };

    const busca = ehBusca(type, props.placeholder);
    const temTexto = String(props.value ?? "").length > 0;
    const mostrarLimpar = busca && temTexto && !props.disabled && !props.readOnly;

    const campo = (
      <input
        type={type}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          mostrarLimpar && "pr-8",
          className,
        )}
        ref={setRef}
        {...props}
      />
    );

    // A estrutura não pode mudar enquanto o usuário digita: trocar o formato do
    // retorno remontaria o input e faria o campo perder o foco na 1ª letra.
    if (!busca) return campo;

    const limpar = () => {
      const el = interno.current;
      if (!el) return;
      const setter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        "value",
      )?.set;
      setter?.call(el, "");
      el.dispatchEvent(new Event("input", { bubbles: true }));
      el.focus();
    };

    return (
      <div className="relative w-full">
        {campo}
        {mostrarLimpar && (
          <button
            type="button"
            aria-label="Limpar busca"
            onClick={limpar}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";

export { Input };
